import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';
import { ReadinessAthlete } from '@/types';

// Register Chart.js components for Radar chart
ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// Individual readiness response type (matches ReadinessScore.tsx)
interface IndividualReadinessResponse {
  readiness_score: number;
  tier: string;
  confidence_adjusted_score: number;
  score_range: {
    lower: number;
    upper: number;
    center: number;
  };
  components?: {
    hrv?: {
      score?: number;
      z_score?: number;
      composite_score: number;
      raw_value?: number;
      baseline_mean?: number;
      unit?: string;
      trend?: number;
      is_estimated?: boolean;  // True if HRV is estimated from BPM (less accurate)
    };
    rhr?: number | {
      score: number;
      raw_value: number;
      unit: string;
      trend: number;
    };
    sleep?: {
      final_score: number;
      duration_score?: number;
      consistency_penalty?: number;
      raw_value?: number;
      avg_duration?: number;
      unit?: string;
      trend?: number;
    };
    acwr?: {
      score: number;
      raw_value?: number;
      unit?: string;
      acute_load?: number;
      chronic_load?: number;
      zone?: string;
    } | null;
    wellness?: {
      composite_score: number;
      hooper_index?: number;
    } | null;
  };
  device_confidence?: {
    overall_confidence: number;
    device_type: string;
  };
  // WHOOP strain data (fallback when ACWR not available)
  strain?: {
    latest: number;
    average: number;
  };
  date: string;
  warnings: string[];
}

// Athlete with individual readiness data
interface AthleteWithReadiness {
  id: number;
  name: string;
  readiness: IndividualReadinessResponse | null;
  loading: boolean;
  error: string | null;
}

interface TeamReadinessAssessmentProps {
  athletes: ReadinessAthlete[];
  isDark: boolean;
  onAthleteClick?: (athleteId: number) => void;
}

interface TeamMetrics {
  hrv: number;
  rhr: number;
  sleep: number;
  acwr: number;
  overall: number;
}

// Progress bar component for individual metrics
const MetricProgressBar: React.FC<{
  label: string;
  value: number;
  maxValue?: number;
  isDark: boolean;
}> = ({ label, value, maxValue = 100, isDark }) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  
  return (
    <div className={`p-4 rounded-xl border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
      <div className="flex justify-between items-center mb-2">
        <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{label}</span>
        <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{value.toFixed(0)}%</span>
      </div>
      <div className={`h-1.5 rounded-full overflow-hidden ${isDark ? 'bg-neutral-700' : 'bg-gray-300'}`}>
        <div 
          className={`h-full rounded-full transition-all duration-500 ${isDark ? 'bg-white' : 'bg-gray-700'}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const getTierLabel = (tier: string) => {
  const tierUpper = tier.toUpperCase();
  switch (tierUpper) {
    case 'OPTIMAL':
    case 'EXCELLENT':
      return 'Optimal';
    case 'MODERATE':
    case 'GOOD':
      return 'Moderate';
    case 'POOR':
      return 'Poor';
    case 'LOW':
      return 'Low';
    default:
      return tier;
  }
};

// Helper to get tier badge styling (aligned with TeamReadinessAssessment score tiers)
const getTierBadgeStyle = (tier: string) => {
  const label = getTierLabel(tier);
  switch (label) {
    case 'Optimal':
      return 'text-green-500 border-transparent';
    case 'Moderate':
      return 'text-yellow-400 border-transparent';
    case 'Poor':
    case 'Low':
      return 'text-red-500 border-transparent';
    default:
      return 'text-gray-400 border-transparent';
  }
};

// Compact athlete card for roster - uses individual readiness data
const AthleteRosterCard: React.FC<{
  athlete: AthleteWithReadiness;
  isDark: boolean;
  onClick?: () => void;
}> = ({ athlete, isDark, onClick }) => {
  // Loading state
  if (athlete.loading) {
    return (
      <div
        className={`rounded-xl p-4 border ${
          isDark 
            ? 'bg-neutral-900/80 border-neutral-700/50' 
            : 'bg-white border-gray-200'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {athlete.name}
          </h4>
          <div className="animate-pulse w-16 h-5 rounded bg-gray-300 dark:bg-gray-700" />
        </div>
        <div className="grid grid-cols-3 gap-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="animate-pulse">
              <div className="w-12 h-3 rounded bg-gray-300 dark:bg-gray-700 mb-1" />
              <div className="w-8 h-5 rounded bg-gray-300 dark:bg-gray-700" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Error state
  if (athlete.error || !athlete.readiness) {
    return (
      <div
        onClick={onClick}
        className={`rounded-xl p-4 border cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
          isDark 
            ? 'bg-neutral-900/80 border-neutral-700/50 hover:border-neutral-600' 
            : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {athlete.name}
          </h4>
          <span className={`text-xs font-medium px-2.5 py-1 rounded-md border bg-gray-500/20 text-gray-400 border-gray-500/30`}>
            NO DATA
          </span>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <AlertCircle className="w-3.5 h-3.5" />
          <span>No wearable data available</span>
        </div>
      </div>
    );
  }

  const { readiness } = athlete;
  const readinessScore = Math.round(readiness.readiness_score);
  
  // Extract ACWR from components (Garmin)
  const acwr = readiness.components?.acwr;
  const acwrValue = acwr?.raw_value ?? null;
  
  // Extract strain from components (WHOOP fallback when ACWR unavailable)
  const strain = readiness.strain;
  const strainValue = strain?.latest ?? null;
  
  // Use ACWR if available, otherwise use WHOOP strain
  const hasAcwr = acwrValue !== null;
  const hasStrain = strainValue !== null && strainValue > 0;
  const loadLabel = hasAcwr ? 'ACWR' : (hasStrain ? 'Strain' : 'Load');
  const loadValue = hasAcwr ? acwrValue : (hasStrain ? strainValue : null);
  
  // Calculate trend based on data source
  const loadTrend = hasAcwr
    ? (acwrValue! > 1.3 ? 'up' : acwrValue! < 0.8 ? 'down' : 'stable')
    : (hasStrain ? (strainValue! > 14 ? 'up' : strainValue! < 8 ? 'down' : 'stable') : 'stable');
  
  // Extract HRV from components
  const hrvData = readiness.components?.hrv;
  const hrv = hrvData?.raw_value ?? hrvData?.baseline_mean ?? 0;
  const hrvIsEstimated = hrvData?.is_estimated ?? false;

  return (
    <div
      onClick={onClick}
      className={`rounded-xl p-4 border cursor-pointer transition-all duration-200 hover:scale-[1.02] ${
        isDark 
          ? 'bg-neutral-900/80 border-neutral-700/50 hover:border-neutral-600' 
          : 'bg-white border-gray-200 hover:border-gray-300 hover:shadow-md'
      }`}
    >
      {/* Header: Name and Badge */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className={`font-semibold text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>
            {athlete.name}
          </h4>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-md border ${getTierBadgeStyle(readiness.tier)}`}>
          {getTierLabel(readiness.tier)}
        </span>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-3 gap-3">
        {/* Readiness */}
        <div>
          <div className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>Readiness</div>
          <div className="flex items-baseline">
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{readinessScore}</span>
            <span className={`text-sm ml-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>%</span>
          </div>
        </div>

        {/* Load (ACWR or WHOOP Strain) */}
        <div>
          <div className={`text-xs mb-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{loadLabel}</div>
          <div className="flex items-center">
            <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
              {loadValue !== null ? (hasAcwr ? loadValue.toFixed(2) : loadValue.toFixed(1)) : '—'}
            </span>
            {loadValue !== null && (
              <span className="ml-1">
                {loadTrend === 'up' ? (
                  <TrendingUp className="w-3.5 h-3.5 text-gray-400" />
                ) : loadTrend === 'down' ? (
                  <TrendingDown className="w-3.5 h-3.5 text-gray-400" />
                ) : (
                  <Minus className="w-3.5 h-3.5 text-gray-400" />
                )}
              </span>
            )}
          </div>
        </div>

        {/* HRV */}
        <div>
          <div className={`text-xs mb-1 flex items-center gap-1 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            HRV
            {hrvIsEstimated && (
              <span 
                className="text-amber-500 cursor-help" 
                title="Estimated from heart rate data (overnight HRV unavailable)"
              >
                ~
              </span>
            )}
          </div>
          <div className="flex items-baseline">
            <span className={`text-lg font-bold ${hrvIsEstimated ? (isDark ? 'text-amber-400/80' : 'text-amber-600/80') : (isDark ? 'text-white' : 'text-gray-900')}`}>
              {Math.round(hrv)}
            </span>
            <span className={`text-sm ml-0.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>ms</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const TeamReadinessAssessment: React.FC<TeamReadinessAssessmentProps> = ({
  athletes,
  isDark,
  onAthleteClick,
}) => {
  // Convert athletes from props to AthleteWithReadiness format
  // Data is already fetched by parent via unified readiness API - no individual API calls needed
  const athletesWithReadiness = useMemo<AthleteWithReadiness[]>(() => {
    if (!athletes || athletes.length === 0) {
      return [];
    }

    return athletes.map(athlete => {
      // Convert the unified ReadinessAthlete data to IndividualReadinessResponse format
      // This maintains backward compatibility with the AthleteRosterCard component
      const hasData = athlete.score !== undefined && athlete.score > 0;
      
      const readiness: IndividualReadinessResponse | null = hasData ? {
        readiness_score: athlete.score * 10, // Convert 0-10 to 0-100 scale
        tier: athlete.tier,
        confidence_adjusted_score: athlete.score * 10,
        score_range: {
          lower: (athlete.score - 0.5) * 10,
          upper: (athlete.score + 0.5) * 10,
          center: athlete.score * 10,
        },
        components: {
          hrv: {
            composite_score: athlete.hrv?.value ? Math.min(100, (athlete.hrv.value / 100) * 100) : 50,
            raw_value: athlete.hrv?.average || athlete.hrv?.value || 0,
            baseline_mean: athlete.hrv?.average || 0,
            trend: athlete.hrv?.trend || 0,
          },
          rhr: {
            score: athlete.rhr?.value ? Math.min(100, Math.max(0, 100 - ((athlete.rhr.value - 40) / 40) * 100)) : 50,
            raw_value: athlete.rhr?.value || 0,
            unit: 'bpm',
            trend: athlete.rhr?.trend || 0,
          },
          sleep: {
            final_score: athlete.sleep?.average_quality || 0,
            raw_value: athlete.sleep_trend?.value || 0,
            avg_duration: athlete.sleep?.average_duration || 0,
            trend: athlete.sleep_trend?.trend || 0,
          },
          acwr: athlete.acwr?.value ? {
            score: athlete.acwr.score || 0,
            raw_value: athlete.acwr.value,
            zone: athlete.acwr.zone || 'unknown',
          } : null,
        },
        // Add strain data for WHOOP fallback when ACWR not available
        strain: athlete.strain ? {
          latest: athlete.strain.latest || 0,
          average: athlete.strain.average || 0,
        } : undefined,
        date: new Date().toISOString(),
        warnings: [],
      } : null;

      return {
        id: athlete.id,
        name: athlete.name,
        readiness,
        loading: false,
        error: hasData ? null : 'No data available',
      };
    });
  }, [athletes]);

  // Calculate team averages from individual readiness data
  const teamMetrics = useMemo<TeamMetrics>(() => {
    const athletesWithData = athletesWithReadiness.filter(a => a.readiness !== null);
    
    if (athletesWithData.length === 0) {
      return { hrv: 0, rhr: 0, sleep: 0, acwr: 0, overall: 0 };
    }

    // Calculate average readiness score
    const avgScore = athletesWithData.reduce((sum, a) => sum + (a.readiness?.readiness_score || 0), 0) / athletesWithData.length;

    // Calculate component averages
    let hrvTotal = 0, hrvCount = 0;
    let rhrTotal = 0, rhrCount = 0;
    let sleepTotal = 0, sleepCount = 0;
    let acwrTotal = 0, acwrCount = 0;

    athletesWithData.forEach(a => {
      const components = a.readiness?.components;
      if (!components) return;

      // HRV score
      if (components.hrv?.composite_score !== undefined) {
        hrvTotal += components.hrv.composite_score;
        hrvCount++;
      }

      // RHR score
      if (components.rhr !== undefined) {
        const rhrScore = typeof components.rhr === 'number' ? components.rhr : components.rhr.score;
        rhrTotal += rhrScore;
        rhrCount++;
      }

      // Sleep score
      if (components.sleep?.final_score !== undefined) {
        sleepTotal += components.sleep.final_score;
        sleepCount++;
      }

      // ACWR score (or strain score as fallback)
      if (components.acwr?.score !== undefined) {
        acwrTotal += components.acwr.score;
        acwrCount++;
      } else if (a.readiness?.strain?.latest) {
        // Convert strain (0-21 scale) to score (0-100)
        // 10.5 is middle of range, so normalize around that
        const strainScore = Math.min(100, Math.max(0, (a.readiness.strain.latest / 21) * 100));
        acwrTotal += strainScore;
        acwrCount++;
      }
    });

    return {
      hrv: hrvCount > 0 ? hrvTotal / hrvCount : 50,
      rhr: rhrCount > 0 ? rhrTotal / rhrCount : 50,
      sleep: sleepCount > 0 ? sleepTotal / sleepCount : 50,
      acwr: acwrCount > 0 ? acwrTotal / acwrCount : 50,
      overall: avgScore,
    };
  }, [athletesWithReadiness]);

  // Determine tier based on overall score
  const getTier = (score: number): { label: string; color: string } => {
    if (score >= 80) return { label: 'Optimal', color: 'text-green-500' };
    if (score >= 60) return { label: 'Moderate', color: 'text-yellow-400' };
    if (score >= 40) return { label: 'Poor', color: 'text-red-500' };
    return { label: 'Low', color: 'text-red-500' };
  };

  const tier = getTier(teamMetrics.overall);

  // Radar chart data
  const radarData = {
    labels: ['HRV', 'RHR', 'Sleep', 'Load'],
    datasets: [
      {
        label: 'Team Average',
        data: [teamMetrics.hrv, teamMetrics.rhr, teamMetrics.sleep, teamMetrics.acwr],
        backgroundColor: isDark ? 'rgba(59, 130, 246, 0.15)' : 'rgba(37, 99, 235, 0.12)',
        borderColor: isDark ? '#3B82F6' : '#2563EB',
        borderWidth: 2,
        pointBackgroundColor: isDark ? '#60A5FA' : '#3B82F6',
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: isDark ? '#60A5FA' : '#3B82F6',
      },
    ],
  };

  // Radar chart options
  const radarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          color: isDark ? '#374151' : '#9CA3AF',
        },
        grid: {
          color: isDark ? '#374151' : '#9CA3AF',
        },
        pointLabels: {
          color: isDark ? '#9CA3AF' : '#1F2937',
          font: {
            size: 14,
            weight: '500' as const,
          },
        },
        ticks: {
          color: isDark ? '#6B7280' : '#374151',
          backdropColor: 'transparent',
          font: {
            size: 10,
          },
          stepSize: 25,
        },
        suggestedMin: 0,
        suggestedMax: 100,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? '#0a0a0a' : '#FFFFFF',
        titleColor: isDark ? '#F9FAFB' : '#111827',
        bodyColor: isDark ? '#D1D5DB' : '#374151',
        borderColor: isDark ? '#374151' : '#9CA3AF',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: (context: any) => ` ${context.label}: ${context.raw.toFixed(1)}%`,
        },
      },
    },
  };

  if (!athletes || athletes.length === 0) {
    return (
      <div className="cgm-metric-card rounded-2xl p-8">
        <div className="text-center cgm-text-secondary">
          <p>No athlete readiness data available</p>
        </div>
      </div>
    );
  }

  // Count athletes by status
  const loadingCount = athletesWithReadiness.filter(a => a.loading).length;
  const withDataCount = athletesWithReadiness.filter(a => a.readiness !== null).length;
  const noDataCount = athletesWithReadiness.filter(a => !a.loading && a.readiness === null).length;

  return (
    <div className="space-y-6">
      {/* Team Roster Section */}
      <div className="cgm-metric-card rounded-2xl p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className={`text-lg font-semibold ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
            Team Roster
          </h3>
          {loadingCount > 0 && (
            <span className="text-xs text-gray-500">
              Loading {loadingCount} athlete{loadingCount > 1 ? 's' : ''}...
            </span>
          )}
          {loadingCount === 0 && noDataCount > 0 && (
            <span className="text-xs text-amber-500">
              {noDataCount} athlete{noDataCount > 1 ? 's' : ''} without data
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {athletesWithReadiness.map((athlete) => (
            <AthleteRosterCard
              key={athlete.id}
              athlete={athlete}
              isDark={isDark}
              onClick={() => onAthleteClick?.(athlete.id)}
            />
          ))}
        </div>
      </div>

      {/* Team Readiness Assessment Section */}
      <div className="cgm-metric-card rounded-2xl p-6">
        {/* Header */}
        <div className="mb-6">
          <h3 className="text-xl font-bold cgm-text-primary">Team Readiness Assessment</h3>
          <p className="text-sm mt-1 cgm-text-secondary">
            Aggregate performance metrics predicting team preparedness based on individual sports science data
          </p>
        </div>

        {/* Main Content - Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Spider Chart */}
          <div className={`rounded-xl p-4 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
            <div className="h-80">
              <Radar data={radarData} options={radarOptions} />
            </div>
          </div>

          {/* Right Column - Overall Score & Progress Bars */}
          <div className="space-y-4">
            {/* Overall Readiness Card */}
            <div className={`rounded-xl p-6 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
              <div className="text-sm mb-1 cgm-text-secondary">Overall Readiness</div>
              <div className="text-5xl font-bold mb-2 cgm-text-primary">
                {teamMetrics.overall.toFixed(0)}%
              </div>
              <div className={`text-lg font-semibold ${tier.color}`}>
                {tier.label}
              </div>
              <div className="text-xs cgm-text-secondary mt-2">
                Based on {withDataCount} of {athletes.length} athletes
              </div>
            </div>

            {/* Individual Metric Progress Bars */}
            <MetricProgressBar label="HRV" value={teamMetrics.hrv} isDark={isDark} />
            <MetricProgressBar label="RHR" value={teamMetrics.rhr} isDark={isDark} />
            <MetricProgressBar label="Sleep" value={teamMetrics.sleep} isDark={isDark} />
            <MetricProgressBar label="Load" value={teamMetrics.acwr} isDark={isDark} />

            {/* Science-Based Metrics Explanation */}
            <div className={`rounded-xl p-4 border ${isDark ? 'border-neutral-700/50' : 'border-gray-200'}`}>
              <h4 className={`text-sm font-semibold mb-3 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Science-Based Metrics</h4>
              <div className="space-y-1.5 text-xs cgm-text-secondary">
                <p>• HRV: Heart rate variability & stress markers</p>
                <p>• RHR: Resting heart rate trends</p>
                <p>• Sleep: Duration & quality scores</p>
                <p>• Load: ACWR (Garmin) or Strain (WHOOP)</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamReadinessAssessment;
